import React, { useRef, useEffect } from 'react';
import './FileTransferBackground.css';

type Mode = 'light' | 'dark';

type Packet = {
  x: number;
  y: number;
  speed: number;
  size: number;
  phase: number;
  pathAngle: number;
  isFalling: boolean;
  fallVelocity: number;
  fallRotation: number;
  rotation: number;
};

interface FileTransferBackgroundProps {
  mode?: Mode;
}

const FileTransferBackground: React.FC<FileTransferBackgroundProps> = ({ mode = 'light' }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const packetsRef = useRef<Packet[]>([]);
  const modeRef = useRef<Mode>(mode);
  modeRef.current = mode;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.clientWidth || window.innerWidth;
    let height = canvas.clientHeight || window.innerHeight;
    let dpr = window.devicePixelRatio || 1;

    const resize = () => {
      width = canvas.clientWidth || window.innerWidth;
      height = canvas.clientHeight || window.innerHeight;
      const rawDpr = window.devicePixelRatio || 1;
      const isLargeScreen = width >= 1920 || height >= 1080;
      const maxDpr = isLargeScreen ? 1 : 1.2;
      dpr = Math.min(rawDpr, maxDpr);

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const packetCount = 10;
    const horizontalBuffer = 80;
    const packets = packetsRef.current;

    const createPacket = (
      options: {
        spawnAtTop?: boolean;
        spawnOnScreen?: boolean;
        spawnFromLeft?: boolean;
      } = {},
    ): Packet => {
      const { spawnAtTop = false, spawnOnScreen = false, spawnFromLeft = false } = options;
      let pathAngle = Math.random() > 0.5 ? Math.PI / 6 : -Math.PI / 6;

      if (spawnFromLeft) {
        pathAngle = Math.random() < 0.5 ? Math.PI / 8 : Math.PI / 6;
      }
      const size = 4 + Math.random() * 3;
      const movingRight = Math.cos(pathAngle) >= 0;
      const startY = spawnAtTop ? -40 - Math.random() * 60 : Math.random() * height;
      const startX = spawnAtTop
        ? Math.random() * width
        : spawnOnScreen
            ? Math.random() * width
            : spawnFromLeft || movingRight
                ? -horizontalBuffer - Math.random() * horizontalBuffer
                : width + horizontalBuffer + Math.random() * horizontalBuffer;
      return {
        x: startX,
        y: startY,
        speed: 0.4 + Math.random() * 0.4,
        size,
        phase: Math.random() * Math.PI * 2,
        pathAngle,
        isFalling: false,
        fallVelocity: 0.6,
        fallRotation: (Math.random() - 0.5) * 0.04,
        rotation: pathAngle * 0.2,
      };
    };

    if (packets.length === 0) {
      packets.push(
        ...Array.from({ length: packetCount }, () => createPacket({ spawnFromLeft: true })),
      );
    }

    const isDark = () => modeRef.current === 'dark';

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      const darkMode = isDark();
      const baseFill = darkMode ? '#000000' : '#ffffff';
      ctx.fillStyle = baseFill;
      ctx.fillRect(0, 0, width, height);

      ctx.save();
      ctx.lineWidth = 1;

      const cyan = darkMode ? '#0f172a' : '#e0f2fe';
      const green = darkMode ? '#1e293b' : '#bbf7d0';
      const alpha1 = darkMode ? 0.15 : 0.08;
      const alpha2 = darkMode ? 0.12 : 0.06;

      ctx.globalAlpha = alpha1;
      ctx.strokeStyle = cyan;
      const step = height / 6;
      for (let i = -1; i <= 6; i++) {
        const y = i * step;
        ctx.beginPath();
        ctx.moveTo(-width * 0.2, y);
        ctx.lineTo(width * 1.2, y + step * 0.4);
        ctx.stroke();
      }

      ctx.globalAlpha = alpha2;
      ctx.strokeStyle = green;
      for (let i = -1; i <= 6; i++) {
        const y = i * step + step / 2;
        ctx.beginPath();
        ctx.moveTo(-width * 0.2, y);
        ctx.lineTo(width * 1.2, y - step * 0.4);
        ctx.stroke();
      }
      ctx.restore();

      ctx.save();

      const { x: mx, y: my } = mouseRef.current;
      packets.forEach((p, index) => {
        if (p.isFalling) {
          p.phase += 0.008;
          p.fallVelocity += 0.14;
          p.y += p.fallVelocity;
          p.x += Math.sin(p.phase) * 0.4;
          p.rotation += p.fallRotation;

          if (p.y - 60 > height) {
            packets[index] = createPacket({ spawnAtTop: true, spawnOnScreen: true });
          }
          return;
        }

        p.phase += 0.02;
        const mouseInfluenceX = (mx - 0.5) * 0.3;
        const mouseInfluenceY = (my - 0.5) * 40;

        p.x += Math.cos(p.pathAngle) * p.speed * (1 + mouseInfluenceX);
        p.y += Math.sin(p.pathAngle) * p.speed;
        p.y += Math.sin(p.phase) * 0.25 + mouseInfluenceY * 0.0015;

        const movingRight = Math.cos(p.pathAngle) >= 0;
        if (movingRight) {
          if (p.x > width + horizontalBuffer) {
            p.x = -horizontalBuffer - Math.random() * horizontalBuffer;
            p.y = Math.random() * height;
          }
        } else if (p.x < -horizontalBuffer) {
          p.x = width + horizontalBuffer + Math.random() * horizontalBuffer;
          p.y = Math.random() * height;
        }
        if (p.y > height + 40) p.y = -40;
        if (p.y < -40) p.y = height + 40;

        p.rotation = p.pathAngle * 0.2 + Math.sin(p.phase) * 0.02;
      });

      ctx.save();
      ctx.globalAlpha = darkMode ? 0.85 : 0.8;
      packets.forEach((p) => {
        if (p.isFalling) {
          return;
        }
        const fileHalfLength = p.size * 2.1; // matches half of the rendered file width
        ctx.beginPath();
        const trailLength = 60;
        const tx = p.x - Math.cos(p.pathAngle) * trailLength;
        const ty = p.y - Math.sin(p.pathAngle) * trailLength;
        const tailEndX = p.x - Math.cos(p.pathAngle) * fileHalfLength;
        const tailEndY = p.y - Math.sin(p.pathAngle) * fileHalfLength;
        const cpOffset = 12;

        const cx = tx + Math.sin(p.phase) * cpOffset;
        const cy = ty - Math.sin(p.phase) * cpOffset;

        ctx.moveTo(tx, ty);
        ctx.quadraticCurveTo(cx, cy, tailEndX, tailEndY);
        const packetGradient = ctx.createLinearGradient(tx, ty, tailEndX, tailEndY);
        if (darkMode) {
          packetGradient.addColorStop(0, 'rgba(240, 226, 226, 0)');
          packetGradient.addColorStop(1, 'rgba(226, 232, 240, 0.85)');
        } else {
          packetGradient.addColorStop(0, 'rgba(56, 189, 248, 0)');
          packetGradient.addColorStop(1, 'rgba(56, 189, 248, 0.6)');
        }
        ctx.strokeStyle = packetGradient;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      });
      ctx.restore();

      const fileFill = darkMode ? 'rgba(23, 68, 193, 0.85)' : 'rgba(15, 23, 42, 0.16)';
      const fileLine = darkMode ? 'rgba(148, 163, 184, 0.85)' : 'rgba(148, 163, 184, 0.9)';

      packets.forEach((p) => {
        const w = p.size * 4.2;
        const h = p.size * 3.0;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = fileFill;
        ctx.beginPath();
        ctx.moveTo(-w / 2, -h / 2);
        ctx.lineTo(w / 2 - 3, -h / 2);
        ctx.lineTo(w / 2, -h / 2 + 3);
        ctx.lineTo(w / 2, h / 2);
        ctx.lineTo(-w / 2, h / 2);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = fileLine;
        ctx.fillRect(-w / 2 + 2, -h / 2 + 3, w - 5, 1);
        ctx.fillRect(-w / 2 + 2, -h / 2 + 6, w * 0.5, 1);

        ctx.restore();
      });

      ctx.restore();

      animationRef.current = window.requestAnimationFrame(render);
    };

    const handleClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      if (clickX < 0 || clickY < 0 || clickX > rect.width || clickY > rect.height) {
        return;
      }

      for (let i = packets.length - 1; i >= 0; i -= 1) {
        const p = packets[i];
        if (p.isFalling) continue;

        const w = p.size * 4.2;
        const h = p.size * 3.0;
        const radius = Math.sqrt(w * w + h * h) * 0.5 + 6;
        const dx = clickX - p.x;
        const dy = clickY - p.y;

        if (dx * dx + dy * dy <= radius * radius) {
          p.isFalling = true;
          p.fallVelocity = Math.max(p.speed * 0.7, 0.6);
          p.fallRotation = (Math.random() - 0.5) * 0.5;
          const maxPackets = 500;
          if (packets.length < maxPackets) {
            const packetsToAdd = Math.min(5, maxPackets - packets.length);
            for (let nb = 0; nb < packetsToAdd; nb += 1) {
              packets.push(createPacket());
            }
          }
          break;
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const widthPixels = rect.width;
      const heightPixels = rect.height;
      if (widthPixels === 0 || heightPixels === 0) {
        return;
      }

      mouseRef.current = {
        x: (e.clientX - rect.left) / widthPixels,
        y: (e.clientY - rect.top) / heightPixels,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: 0.5, y: 0.5 };
    };

    window.addEventListener('click', handleClick, true);
    window.addEventListener('mousemove', handleMouseMove, true);
    window.addEventListener('mouseout', handleMouseLeave, true);
    render();

    return () => {
      if (animationRef.current !== null) {
        window.cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resize);
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('mousemove', handleMouseMove, true);
      window.removeEventListener('mouseout', handleMouseLeave, true);
    };
  }, []);

  return (
    <div className="file-transfer-bg">
      <canvas ref={canvasRef} className="file-transfer-bg-canvas" />
    </div>
  );
};

export default FileTransferBackground;
